/**
 * Entry point for /users/csv-create — bulk create 8x8 users from CSV.
 */
import React from 'react';
import { Box } from '@chakra-ui/react';
import { useSelector } from 'react-redux';
import { Error } from 'components/index.js';
import { nullError } from 'components/error.js';
import appConfig from '../../../../config.json';

import useCSVCreateUsers, {
    CREATE_SUCCESS_MESSAGE,
} from './useCSVCreateUsers.js';
import useCSVCreateBulkUpdate from './useCSVCreateBulkUpdate.js';
import useCSVCreateTemplate from './useCSVCreateTemplate.js';

import CSVTemplateView from './CSVTemplateView.jsx';
import VerifyCSVView from './VerifyCSVView.jsx';
import VerifyCSVTableView from './VerifyCSVTableView.jsx';
import APILoadingView from './APILoadingView.jsx';
import APIResultsView from './APIResultsView.jsx';

import './reset.css';

const DOWNLOAD_CSV_TEMPLATE = 'DOWNLOAD_CSV_TEMPLATE';
const LOADING_CSV_VALIDATION = 'LOADING_CSV_VALIDATION';
const CSV_VALIDATION_RESULTS = 'CSV_VALIDATION_RESULTS';
const API_CALLS_PROGRESS = 'API_CALLS_PROGRESS';
const API_CALLS_RESULTS = 'API_CALLS_RESULTS';
const CSV_DATA_NOT_FOUND = 'CSV_DATA_NOT_FOUND';
const CSV_LIMIT_REACHED = 'CSV_LIMIT_REACHED';

// Renders the internal 'RingGroup' / 'User' account type as the CSV-facing label
const renderAccountType = (rowData) =>
    rowData.accountType === 'RingGroup' ? 'Ring Group' : 'User';

// Columns shown in the verification table for the create flow
const passedColumns = [
    { title: 'Account Type', field: 'accountType', render: renderAccountType },
    { title: 'First Name', field: 'firstName' },
    { title: 'Last Name', field: 'lastName' },
    { title: 'Email', field: 'email' },
    { title: 'Site', field: 'site' },
    { title: 'Platform Extension', field: 'platformExtension' },
    { title: 'Verification Status', field: 'verificationStatus' },
];

const failedColumns = [
    { title: 'Account Type', field: 'accountType', render: renderAccountType },
    { title: 'First Name', field: 'firstName' },
    { title: 'Last Name', field: 'lastName' },
    { title: 'Email', field: 'email' },
    { title: 'Site', field: 'site' },
    { title: 'Platform Extension', field: 'platformExtension' },
    {
        title: 'Failure Reasons',
        field: 'errors',
        width: '40%',
        render: (rowData) => {
            if (Array.isArray(rowData.errors) && rowData.errors.length > 0) {
                return (
                    <span style={{ whiteSpace: 'pre-line' }}>
                        {rowData.errors.join('\n')}
                    </span>
                );
            }
            return null;
        },
    },
];

const succeedAPIColumns = [{ title: 'Email', field: 'email' }];

const failedAPIColumns = [
    { title: 'Email', field: 'email' },
    { title: 'Failure Reasons', field: 'apiErrors' },
];

function useCreateCSVDisplayControl() {
    const [viewVerificationResults, setViewVerificationResults] =
        React.useState(false);
    const [viewAPIResults, setViewAPIResults] = React.useState(false);

    const {
        handleUploadUsersData,
        uploadData,
        verificationChecks,
        loading: verificationLoading,
        error: verificationErrors,
        payloads,
        verificationResult,
        handleReset: handleResetCSV,
        handleExportInvalidData,
        csvLimit,
    } = useCSVCreateUsers();

    const {
        handlePayloadChange,
        loading: apiLoading,
        counter: completionCounter,
        errorCounter,
        error: apiErrors,
        succeed: apiSucceed,
        handleExportFailedData,
        handleReset: handleResetBulkUpdate,
        total,
    } = useCSVCreateBulkUpdate();

    const handleViewVerificationResults = () =>
        setViewVerificationResults(true);
    const handleViewAPIResults = () => setViewAPIResults(true);

    const handleStartOver = () => {
        setViewVerificationResults(false);
        setViewAPIResults(false);
        handleResetCSV();
        handleResetBulkUpdate();
        window.location.reload();
    };

    const handleCurrentDisplay = () => {
        if (verificationErrors) return CSV_DATA_NOT_FOUND;
        if (csvLimit) return CSV_LIMIT_REACHED;
        if (!uploadData) return DOWNLOAD_CSV_TEMPLATE;
        if (viewAPIResults) return API_CALLS_RESULTS;
        if (apiLoading) return API_CALLS_PROGRESS;
        if (viewVerificationResults) return CSV_VALIDATION_RESULTS;
        if (verificationLoading) return LOADING_CSV_VALIDATION;
    };

    const handleCancelValidationTable = () => {
        handleResetCSV();
        setViewVerificationResults(false);
    };

    const handleStartAPICall = () => {
        handlePayloadChange(payloads);
        setViewVerificationResults(false);
    };

    return {
        handleUploadUsersData,
        handleCurrentDisplay,
        verificationChecks,
        verificationErrors,
        handleViewVerificationResults,
        verificationResult,
        handleCancel: handleCancelValidationTable,
        handleImport: handleStartAPICall,
        handleExportInvalidData,
        completionCounter,
        payloads,
        errorCounter,
        handleViewAPIResults,
        apiErrors,
        apiSucceed,
        handleExportFailedData,
        handleStartOver,
        total,
        csvLimit,
    };
}

export default function CreateCSVDisplay() {
    const {
        handleUploadUsersData,
        handleCurrentDisplay,
        verificationChecks,
        handleViewVerificationResults,
        verificationResult,
        handleCancel,
        verificationErrors,
        handleImport,
        handleExportInvalidData,
        completionCounter,
        payloads,
        errorCounter,
        handleViewAPIResults,
        apiErrors,
        apiSucceed,
        handleExportFailedData,
        handleStartOver,
        total,
        csvLimit,
    } = useCreateCSVDisplayControl();

    const { handleDownloadTemplate, loading: templateLoading } =
        useCSVCreateTemplate();

    const { currentCompany } = useSelector((state) => state.navigationLists);

    if (!currentCompany) {
        return <Error error={nullError} />;
    }

    const currentDisplay = handleCurrentDisplay();

    return (
        <>
            <Box id="csv-create">
                {(currentDisplay === DOWNLOAD_CSV_TEMPLATE ||
                    currentDisplay === CSV_LIMIT_REACHED) && (
                    <CSVTemplateView
                        handleUploadUsersData={handleUploadUsersData}
                        enablePlatformSelection={false}
                        isUserPage={true}
                        downloadCSVText="Download Template"
                        header="CSV Create - Users"
                        step1Text="Step 1 - Download the blank CSV template"
                        step2Text="Step 2 - Fill in user details and upload"
                        instructionsHeader="Instructions"
                        handleDownloadTemplate={handleDownloadTemplate}
                        loading={templateLoading}
                        bgColor="white"
                        instructionsBody={[
                            `CSV upload is limited to a maximum of ${appConfig.csvLimit} users per upload.`,
                            'Account Type accepts "User" or "Ring Group" and defaults to "User" if left blank.',
                            'For User rows, required columns are: First Name, Last Name, Email, Site.',
                            'For Ring Group rows, required columns are: First Name (used as the group name), Email, Site. Platform Extension is optional and must contain only numbers.',
                            'Display Name is optional and will be auto-generated as "First Name Last Name" if left blank.',
                            'Site must match the exact site name configured in your 8x8 account.',
                            'Duplicate email addresses within the same upload will be rejected.',
                            'You are responsible for the changes you are about to make through this data import.',
                        ]}
                        csvLimit={csvLimit}
                    />
                )}
                {currentDisplay === LOADING_CSV_VALIDATION && (
                    <VerifyCSVView
                        handleViewVerificationResults={
                            handleViewVerificationResults
                        }
                        verificationChecks={verificationChecks}
                    />
                )}
                {currentDisplay === CSV_VALIDATION_RESULTS && (
                    <VerifyCSVTableView
                        verificationResult={verificationResult}
                        handleCancel={handleCancel}
                        handleImport={handleImport}
                        handleExportInvalidData={handleExportInvalidData}
                        handleStartOver={handleStartOver}
                        passedColumns={passedColumns}
                        failedColumns={failedColumns}
                    />
                )}
                {currentDisplay === API_CALLS_PROGRESS && (
                    <APILoadingView
                        completionCounter={completionCounter}
                        payloads={payloads}
                        verificationResult={verificationResult}
                        errorCounter={errorCounter}
                        handleViewAPIResults={handleViewAPIResults}
                        total={total}
                    />
                )}
                {currentDisplay === API_CALLS_RESULTS && (
                    <APIResultsView
                        error={apiErrors}
                        succeed={apiSucceed}
                        succeedAPIColumns={succeedAPIColumns}
                        failedAPIColumns={failedAPIColumns}
                        handleExportFailedData={handleExportFailedData}
                        handleDone={handleStartOver}
                    />
                )}
                {currentDisplay === CSV_DATA_NOT_FOUND && (
                    <VerifyCSVView
                        handleStartOver={handleStartOver}
                        errors={verificationErrors}
                    />
                )}
            </Box>
        </>
    );
}
